import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { REQUEST_META_DATA } from '@seech-sub/core-ng';
import { Observable } from 'rxjs';
import { OperationsApiRoutes } from '../constants/operations.constants';
import {
  MerchantAdministrationItem,
  MerchantAdministrationPage,
  MerchantAdministrationSummary,
  MerchantFinancialOversight,
  MerchantLifecycleCommand,
  MerchantVerificationCommand,
  MerchantOfferingGovernanceItem,
  MerchantOfferingGovernancePage,
  MerchantOfferingFulfillmentBehaviorCommand,
} from '../models';

@Injectable({ providedIn: 'root' })
export class MerchantAdministrationClientService {
  private readonly http = inject(HttpClient);
  private readonly quietContext = new HttpContext().set(REQUEST_META_DATA.HIDE_PROGRESS, true);

  search(
    searchTerm?: string,
    statusKey?: string,
    isVerified?: boolean,
    page = 1,
    pageSize = 25,
  ): Observable<MerchantAdministrationPage> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (searchTerm) params = params.set('searchTerm', searchTerm);
    if (statusKey) params = params.set('statusKey', statusKey);
    if (isVerified !== undefined) params = params.set('isVerified', isVerified);
    return this.http.get<MerchantAdministrationPage>(OperationsApiRoutes.merchants, {
      params,
      context: this.quietContext,
    });
  }

  summary(): Observable<MerchantAdministrationSummary> {
    return this.http.get<MerchantAdministrationSummary>(OperationsApiRoutes.merchantSummary, {
      context: this.quietContext,
    });
  }

  financials(merchantId?: string, currencyCode?: string, page = 1, pageSize = 10): Observable<MerchantFinancialOversight> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (merchantId) params = params.set('merchantId', merchantId);
    if (currencyCode) params = params.set('currencyCode', currencyCode);
    return this.http.get<MerchantFinancialOversight>(OperationsApiRoutes.merchantFinancials, {
      params,
      context: this.quietContext,
    });
  }

  verify(id: string, request: MerchantVerificationCommand): Observable<MerchantAdministrationItem> {
    return this.http.post<MerchantAdministrationItem>(OperationsApiRoutes.merchantVerify(id), request);
  }

  updateLifecycle(id: string, request: MerchantLifecycleCommand): Observable<MerchantAdministrationItem> {
    return this.http.put<MerchantAdministrationItem>(OperationsApiRoutes.merchantLifecycle(id), request);
  }

  offerings(merchantId: string): Observable<MerchantOfferingGovernancePage> {
    return this.http.get<MerchantOfferingGovernancePage>(OperationsApiRoutes.merchantOfferings(merchantId), {
      params: new HttpParams().set('page', 1).set('pageSize', 100),
      context: this.quietContext,
    });
  }

  updateFulfillmentBehavior(
    merchantId: string,
    offeringId: string,
    request: MerchantOfferingFulfillmentBehaviorCommand,
  ): Observable<MerchantOfferingGovernanceItem> {
    return this.http.put<MerchantOfferingGovernanceItem>(
      OperationsApiRoutes.merchantOfferingFulfillmentBehavior(merchantId, offeringId),
      request,
    );
  }
}
